import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../components/ui/ToastProvider';
import { parseGoogleMapsPlaceUrl, reverseGeocode } from '../../../services/geocodingService';
import { submitProcessor } from '../services/processorService';
import { PROCESSOR_SERVICE_OPTIONS } from '../utils/processorList';
import './AddProcessorPanel.css';

const EMPTY_FORM = {
  name: '',
  phone: '',
  town: '',
  address: '',
  notes: '',
  known_how: '',
  services: [],
  latitude: null,
  longitude: null,
  maps_url: '',
};

function formFromSeed(seed) {
  if (!seed) return { ...EMPTY_FORM, services: [] };
  const latitude = Number(seed.latitude);
  const longitude = Number(seed.longitude);
  return {
    name: seed.name || '',
    phone: seed.phone || '',
    town: seed.town || '',
    address: seed.address || '',
    notes: seed.notes || '',
    known_how: '',
    services: Array.isArray(seed.services) ? [...seed.services] : [],
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    maps_url: '',
  };
}

export function hasProcessorMapPin({ latitude, longitude } = {}) {
  if (latitude == null || longitude == null || latitude === '' || longitude === '') return false;
  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

export function canSubmitProcessorForm({ name, phone, town } = {}) {
  return (
    String(name || '').trim().length > 2
    && String(phone || '').trim().length > 6
    && String(town || '').trim().length > 2
  );
}

export function processorSubmitErrorMessage(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') {
    return 'Could not send this listing. Try again in a moment.';
  }
  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  const firstKey = Object.keys(data).find((key) => key !== 'debug_info');
  const firstVal = firstKey ? data[firstKey] : null;
  if (Array.isArray(firstVal) && firstVal[0]) return String(firstVal[0]);
  if (typeof firstVal === 'string' && firstVal.trim()) return firstVal;
  return 'Could not send this listing. Try again in a moment.';
}

export function AddProcessorPanel({ open, seed, onClose }) {
  const { show: showToast } = useToast();
  const [form, setForm] = useState(() => formFromSeed(seed));
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pasteLoading, setPasteLoading] = useState(false);
  const canSubmit = useMemo(() => canSubmitProcessorForm(form), [form]);
  const pinned = hasProcessorMapPin(form);

  const seedKey = seed?.id || '';
  useEffect(() => {
    if (!open) return undefined;
    setForm(formFromSeed(seed));
    setSent(false);
    setSubmitting(false);
    setPasteLoading(false);
    return undefined;
    // Prefill once per open/seed, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedKey]);

  if (!open) return null;

  const applyMapsLink = async () => {
    const parsed = parseGoogleMapsPlaceUrl(form.maps_url);
    if (!parsed) {
      showToast('Paste a Google Maps link to a specific place.', { duration: 2600 });
      return;
    }
    setPasteLoading(true);
    try {
      const reversed = await reverseGeocode(parsed.longitude, parsed.latitude);
      const townFromPin = reversed
        ? [reversed.city, reversed.state].filter(Boolean).join(', ')
        : '';
      setForm((prev) => ({
        ...prev,
        name: prev.name.trim() ? prev.name : (parsed.name || prev.name),
        address: reversed?.address || prev.address,
        town: prev.town.trim() ? prev.town : (townFromPin || prev.town),
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      }));
    } catch (_) {
      setForm((prev) => ({
        ...prev,
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      }));
    } finally {
      setPasteLoading(false);
    }
  };

  const setField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const toggleService = (label) => {
    setForm((prev) => {
      const on = prev.services.includes(label);
      return {
        ...prev,
        services: on ? prev.services.filter((item) => item !== label) : [...prev.services, label],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      showToast('NAME, PHONE, AND TOWN ARE REQUIRED', { duration: 2600 });
      return;
    }
    setSubmitting(true);
    try {
      const parsedPin = pinned
        ? { latitude: Number(form.latitude), longitude: Number(form.longitude) }
        : parseGoogleMapsPlaceUrl(form.maps_url);
      const latitude = Number.isFinite(parsedPin?.latitude) ? parsedPin.latitude : null;
      const longitude = Number.isFinite(parsedPin?.longitude) ? parsedPin.longitude : null;
      await submitProcessor({
        name: form.name.trim(),
        phone: form.phone.trim(),
        town: form.town.trim(),
        address: form.address.trim() || null,
        services: form.services,
        notes: form.notes.trim(),
        known_how: form.known_how.trim(),
        mapbox_id: seed?.id || null,
        latitude,
        longitude,
        maps_url: form.maps_url.trim() || null,
      });
      setSent(true);
    } catch (err) {
      showToast(processorSubmitErrorMessage(err), { variant: 'danger' });
    } finally {
      setSubmitting(false);
    }
  };

  const sentName = [form.name.trim(), form.town.trim()].filter(Boolean).join(' in ');

  return (
    <div className="pap" data-testid="add-processor-panel">
      <button type="button" className="pap__backdrop" aria-label="Close" onClick={onClose} />
      <div className="pap__sheet" role="dialog" aria-modal="true" aria-labelledby="pap-title">
        <div className="pap__header">
          <span id="pap-title">ADD A PROCESSOR · FOR APPROVAL</span>
          <button type="button" className="pap__close" onClick={onClose}>
            CLOSE
          </button>
        </div>

        {sent ? (
          <div className="pap__success" data-testid="add-processor-success">
            <div className="pap__tick" aria-hidden>✓</div>
            <h2 className="pap__success-title">Sent for approval</h2>
            <p className="pap__success-body">
              {sentName || 'This listing'}
              {' '}
              goes to a Yona admin. They will call to confirm, add a map pin if you did not have
              one, and you will get a notification when it is live.
            </p>
            <button type="button" className="pap__done" onClick={onClose}>
              DONE
            </button>
          </div>
        ) : (
          <form className="pap__form" onSubmit={handleSubmit}>
            <p className="pap__intro">
              Name, phone, and town are enough for a Yona admin to call and confirm. If they are
              not on a map, leave the street blank — we will pin it after that call so other
              hunters can get directions.
            </p>

            <label className="pap__field">
              <span>Business name</span>
              <input
                value={form.name}
                onChange={setField('name')}
                placeholder="Tolliver Deer Processing"
              />
            </label>

            <div className="pap__row">
              <label className="pap__field">
                <span>Phone</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={setField('phone')}
                  placeholder="(334) 555-0142"
                />
              </label>
              <label className="pap__field">
                <span>Town, state</span>
                <input
                  value={form.town}
                  onChange={setField('town')}
                  placeholder="Linden, AL"
                />
              </label>
            </div>

            <label className="pap__field">
              <span>Street address (optional)</span>
              <input
                value={form.address}
                onChange={setField('address')}
                placeholder="1142 County Road 19"
              />
            </label>

            <div className="pap__field">
              <span>Google Maps link (optional)</span>
              <div className="pap__maps">
                <input
                  value={form.maps_url}
                  onChange={setField('maps_url')}
                  placeholder="https://maps.google.com/maps/place/..."
                  aria-label="Google Maps link"
                />
                <button
                  type="button"
                  className="pap__maps-apply"
                  onClick={applyMapsLink}
                  disabled={pasteLoading}
                >
                  {pasteLoading ? 'PINNING…' : 'USE LINK'}
                </button>
              </div>
              <p className="pap__pin" data-testid="add-processor-pin">
                {pinned
                  ? 'Map pin saved. An admin will still verify before this goes live.'
                  : 'No map pin yet. An admin will add directions after they call and confirm.'}
              </p>
            </div>

            <div className="pap__field">
              <span>Services</span>
              <div className="pap__pills">
                {PROCESSOR_SERVICE_OPTIONS.map((label) => {
                  const on = form.services.includes(label);
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`pap__pill${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleService(label)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="pap__field">
              <span>Drop-off notes</span>
              <textarea
                rows={3}
                value={form.notes}
                onChange={setField('notes')}
                placeholder="Cooler is round back, hang your tag on the ear. Call first on weekends."
              />
            </label>

            <label className="pap__field">
              <span>How do you know them?</span>
              <input
                value={form.known_how}
                onChange={setField('known_how')}
                placeholder="Took two deer there last season"
              />
            </label>

            <p className="pap__legal">
              Your name goes on the submission so an admin can follow up. It is not shown publicly
              on the listing.
            </p>

            <button
              type="submit"
              className={`pap__submit${canSubmit ? '' : ' is-disabled'}`}
              aria-disabled={!canSubmit}
              disabled={submitting}
            >
              {submitting ? 'SENDING…' : 'SEND FOR APPROVAL'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
